<template>
    <div>
        <div ref="turnstileEl"></div>
        <p v-if="captchaError" class="text-danger mt-1" role="alert">
            <small>{{ captchaError }}</small>
        </p>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
    import { t } from '@/utils/i18n-helper'

    const props = defineProps({
        siteKey: {
            type: String,
            required: true
        },
        // i18n keys for the three failure states. They default to the generic
        // captcha namespace so a consumer with its own copy can override them
        // without this primitive knowing any business domain.
        loadErrorKey: {
            type: String,
            default: 'captcha.error_captcha'
        },
        verifyErrorKey: {
            type: String,
            default: 'captcha.error_verificar_captcha'
        },
        expiredErrorKey: {
            type: String,
            default: 'captcha.error_expirado_captcha'
        }
    })

    // Consumers must learn about expiry/error, not just see the message: a
    // token that expires while the user is still typing must stop counting as
    // a valid one, otherwise the form would submit a stale token.
    const emit = defineEmits(['success', 'expired', 'error'])
    const turnstileEl = ref(null)
    const widgetId = ref(null)
    const retryCount = ref(0)
    const MAX_RETRIES = 50

    const captchaErrorKey = ref(null)
    const captchaError = computed(() => {
        if (!captchaErrorKey.value) return null
        return t(captchaErrorKey.value)
    })

    // Kept so the pending retry can be cancelled on unmount; otherwise the
    // component keeps polling for window.turnstile after it is gone.
    let retryTimer = null

    const renderTurnstile = () => {
        if (!window.turnstile || !turnstileEl.value) {
            if (retryCount.value >= MAX_RETRIES) {
                captchaErrorKey.value = props.loadErrorKey;
                // Technical diagnostics stay in development; the user still gets
                // the localized message either way.
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Turnstile script failed to load after', MAX_RETRIES, 'attempts')
                }
                return
            }
            
            retryCount.value++
            retryTimer = setTimeout(renderTurnstile, 100)
            return
        }

        retryCount.value = 0

        widgetId.value = window.turnstile.render(turnstileEl.value, {
            sitekey: props.siteKey,
            callback: onSuccess,
            'error-callback': onError,
            'expired-callback': onExpired
        })
    }
    const onSuccess = token => {
        captchaErrorKey.value = null
        emit('success', token)
    }

    const onError = () => {
        captchaErrorKey.value = props.verifyErrorKey;
        emit('error')
    }

    const onExpired = () => {
        captchaErrorKey.value = props.expiredErrorKey;
        emit('expired')
    }

    /**
     * Resets the widget so a fresh challenge can be solved. The failure message
     * is dropped by default: a full reset means starting over. Pass
     * keepFeedback when the reset exists to make a failed challenge solvable
     * again, so the user still sees why the token was invalidated.
     */
    const reset = ({ keepFeedback = false } = {}) => {
        if (widgetId.value !== null && window.turnstile) {
            window.turnstile.reset(widgetId.value)
        }

        if (!keepFeedback) {
            captchaErrorKey.value = null
        }
    }

    onMounted(renderTurnstile)

    onBeforeUnmount(() => {
        if (retryTimer !== null) {
            clearTimeout(retryTimer)
            retryTimer = null
        }
        if (widgetId.value !== null && window.turnstile) {
            window.turnstile.remove(widgetId.value)
        }
    })


    defineExpose({ reset })
</script>
